import { AccountCrawlingTypes } from '@modules/admin-panel/modules/admin-accounts/models/admin-account.model';
import { IUnusedResourcesModalTypes } from '@modules/unused-resources/interfaces/modals.interface';
import { IOnboardingModalTypes } from '@modules/onboarding/interfaces/onboarding-modal.interface';
import { IPartnerModalTypes } from '@modules/partner-portal/interfaces/partner-modals.interface';
import { IAdminModalTypes } from '@modules/admin-panel/domain/models/admin-modal';
import { IEbsModalTypes } from '@modules/ebs/interfaces/ebs-modals.interface';

export enum LocalStorageKeys {
	SIDEBAR_EXPANDED = 'CLOUDVISOR_SIDEBAR_EXPANDED',
	HAS_MANAGED = 'ZD_HAS_MANAGED',
	ONBOARDING_QUEUE = 'ONBOARDING_QUEUE',
}

export enum SearchFilterType {
	ec2Explorer = 'ec2Explorer',
	s3Explorer = 's3Explorer',
}

export enum ApiUrlServices {
	account = '/frontend/account',
	customer = '/frontend/customer',
	frontendCv = '/frontend/cv',
	cv = '/cv-api',
	onboard = '/onboard',
}

export interface CEProjectionAPIResp {
	ce_projection: CEProjection[];
}

export interface CEProjection {
	date: string;
	effective: number;
	ondemand: number;
	savingsTotal: number;
	coverage?: number;
}

export interface CEMetricsParams {
	entity: string;
	interval: string;
	start?: string;
	end?: string;
	days_ago?: number;
	account_id?: string;
}

export interface CEMetricsAPIResponse {
	'ce-report'?: CEMetrics[];
	report: ICURReport;
}

interface ICURReport {
	data: CEMetrics[];
	lastUpdateTime: string;
	status: ICURReportStatus;
}

interface ICURReportStatus {
	partialResponseReason: ICURReportError | null;
	isDatasetFull: boolean;
	isError: boolean;
}

export interface ICURReportError {
	errorCode: number;
	metadata: {
		bucketLink?: string;
	};
}

export interface CEMetrics {
	_origin: string;
	date: string;
	live: 0 | 1; // 0 - point is real
	ondemand: number;
	ondemandRateMinimum: number;
	ondemandUncovered: number;
	ondemandCovered: number;
	ondemandCoveredRate: number;
	ondemandCoveredCSP: number;
	ondemandCoveredCRI1YR: number;
	ondemandCoveredCRI3YR: number;
	ondemandCoveredSP: number;
	ondemandCoveredSP1YR: number;
	ondemandCoveredSP3YR: number;
	ondemandCoveredSRI: number;
	ondemandCoveredRI: number;
	ondemandCoveredEC2SP: number;
	ondemandCoveredSMSP: number;
	hourly: number;
	effective: number;
	effectiveRI: number;
	effectiveSP: number;
	effectiveSelf: number;
	effectiveReal: number;
	savingsTotal: number;
	savingsManaged: number;
	savingsSelf: number;
	savingsSelfRate: number;
	savingsRI: number;
	savingsSP: number;
	totalCommitmentSP: number;
	usedCommitmentSP: number;
	uncoveredAll: number;
	effectiveAll: number;
	ebs: number;
	fargateOndemand: number;
	lambdaOndemand: number;
	platforms: {
		[key: string]: ICePlatform;
	};
	coverage: number;
	coverageCurrent: number;
}

export interface ICePlatform {
	effective: number;
	ondemand: number;
	effective_sp: number;
	effective_ri: number;
	name: string;
}

export interface CEMetricsData {
	date: string[];
	data: {
		ondemand: IMetric;
		effective: IMetric;
		savings: IMetric;
		coverage: IMetric;
	};
}

export interface IMetric {
	potentialCost: MetricsCost[];
	projectedCost: MetricsCost[];
	today: MetricsCost[];
	totalValue: number;
	desc: string;
	tips: string;
	sign: string;
	label: string;
	tooltipPotential: string;
	tooltipProjected: string;
	tooltipToday: string;
}

export interface MetricsCost {
	date: string;
	value: number;
}

export enum MetricsTimeRange {
	monthToDate = 'Month to date',
	thisMonth = 'End of month (projection)',
	prevMonth = 'Previous month',
	last3Month = 'Last 3 months',
	last6Month = 'Last 6 months',
	lastYear = 'Last year',
	customRange = 'Custom range',
}

export interface LiveBillingAPI {
	'live-billing': LiveBilling[];
}

export interface LiveBilling {
	coverage: number;
	date: string;
	effective_mtd: number;
	effective_rate: number;
	es_data_exist: boolean;
	ondemand_covered: number;
	ondemand_covered_rate: number;
	ondemand_mtd: number;
	ondemand_rate: number;
	ondemand_rate_minimum: number;
	platforms: {
		[key: string]: {
			effective: number;
			name: string;
		};
	};
	savings_mtd: number;
	savings_rate: number;
}

export enum IOperativeUserRole {
	premium = 'Premium',
	reseller = 'Reseller',
	partner = 'Partner',
	csm = 'CSM',
	salesManager = 'Sales-manager',
	sales = 'Sales',
	riBuyers = 'RI-Buyers',
	admin = 'Admin',
}

export interface IResponseMessage {
	message: string;
}

export enum ServerMessages {
	success = 'success',
}

export interface IPartialAccountForPermission {
	id: string;
	alias: string;
}

export interface IAccountsPermitted {
	allow: IPartialAccountForPermission[];
	deny: IPartialAccountForPermission[];
}

export interface IAccount {
	account_alias: string;
	account_uuid: string;
	billing_bucket: string;
	company: string;
	crawling: {
		[key in AccountCrawlingTypes]?: number;
	};
	credentials: { role_arn: string };
	cv_standalone: boolean;
	id: string;
	instances_generation: unknown;
	is_aispl: boolean;
	is_managed: boolean;
	master_payer_id: string;
	old_generation: boolean;
	provider: string;
	role_arn: string;
	schedule: { active: boolean };
	active: boolean;
	skip_marketplace: boolean;
	status: number;
	zd_enabled: boolean;
	legal_entity_human?: string;
	product_permissions?: string[];
}

export interface IModalComponent<T> {
	data: IModalData<T>;
	closeEvent: (e?: any) => void;
}

export enum IModals {
	endSession = 'endSession',
	addUser = 'addUser',
	renameAccount = 'renameAccount',
}

export interface IModalData<T> {
	type: ModalTypes;
	title?: string;
	options?: T;
}

export type ModalTypes =
	| IEbsModalTypes
	| IPartnerModalTypes
	| IAdminModalTypes
	| IUnusedResourcesModalTypes
	| IOnboardingModalTypes
	| IModals;

export interface IKPICard {
	title: string;
	value: number;
	tips: string;
	icon?: string;
	sign?: string;
	postfix?: string;
	serverKey?: string;
}

export interface KeyValueString {
	key: string;
	value: string;
}

export enum AuthRoutes {
	login = 'login',
	registration = 'registration',
	forgotPassword = 'forgot-password',
	invited = 'join',
	handleUserProfile = 'handle-user-profile',
}

export interface ProgressBar {
	value: number;
	accountsTotal: number;
	accountsReady: number;
}

export interface RadioItem {
	name: string;
	value: string;
}

export interface IExportResponse {
	link: string;
}

export type DeepPartial<T> = {
	[P in keyof T]?: DeepPartial<T[P]>;
};
